import React, { useRef, useState } from "react";
import { Button, Dropdown, Form, Icon } from "semantic-ui-react";
import { Disclosure, EvidenceDisclosure, SourceLinks, StatusBadge } from "./PiEvidence";

const DD_OPTIONS = [
  { key: "open", value: "open", text: "Open" },
  { key: "in_progress", value: "in_progress", text: "In progress" },
  { key: "done", value: "done", text: "Done" },
  { key: "not_needed", value: "not_needed", text: "Not needed" },
];

function SectionEditor({ section, onEdit, onDone }) {
  // Agents edit the ORIGINAL paragraphs (citations kept); the presentation's
  // cleanup only affects display, never what is stored.
  const [paragraphs, setParagraphs] = useState((section.originalParagraphs || section.paragraphs).map((p) => ({ text: p.text, sourceIds: p.sourceIds || [] })));
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    try {
      await onEdit({ type: "section", key: section.key, paragraphs: paragraphs.filter((p) => p.text.trim()) });
      onDone();
    } finally {
      setSaving(false);
    }
  }
  return (
    <Form>
      {paragraphs.map((p, i) => (
        <Form.TextArea key={i} rows={4} value={p.text} onChange={(e, { value }) => setParagraphs((prev) => prev.map((x, j) => (j === i ? { ...x, text: value } : x)))} />
      ))}
      <Button size="mini" basic icon="plus" content="Add paragraph" onClick={() => setParagraphs((prev) => [...prev, { text: "", sourceIds: [] }])} />
      <p className="pi-small pi-muted" style={{ margin: "6px 0" }}>Your edits keep each paragraph&apos;s citations. The original research text is preserved separately.</p>
      <Button size="small" primary loading={saving} onClick={save} content="Save section" />
      <Button size="small" basic disabled={saving} onClick={onDone} content="Cancel" />
    </Form>
  );
}

function CommentaryEditor({ report, onEdit }) {
  const current = report.agentEdits?.commentary?.text || "";
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(current);
  const [saving, setSaving] = useState(false);
  return (
    <div className="pi-note neutral" style={{ display: "block", marginTop: 24 }}>
      <div className="pi-label" style={{ marginBottom: 6 }}><Icon name="user" color="blue" /> Your commentary</div>
      {editing ? (
        <Form>
          <Form.TextArea rows={4} value={text} onChange={(e, { value }) => setText(value)} placeholder="Your own perspective on this property" />
          <Button size="small" primary loading={saving} onClick={async () => { setSaving(true); try { await onEdit({ type: "commentary", text }); setEditing(false); } finally { setSaving(false); } }} content="Save" />
          <Button size="small" basic onClick={() => setEditing(false)} content="Cancel" />
        </Form>
      ) : (
        <>
          <p style={{ whiteSpace: "pre-wrap", margin: 0 }} className={current ? "" : "pi-muted"}>{current || "No commentary added yet."}</p>
          {onEdit && <button type="button" className="pi-link pi-small" style={{ marginTop: 6 }} onClick={() => setEditing(true)}>{current ? "Edit commentary" : "Add commentary"}</button>}
        </>
      )}
    </div>
  );
}

function Diagnostics({ d }) {
  if (!d) return null;
  const fmt = (t) => (t ? new Date(t).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "-");
  return (
    <Disclosure label="Research Diagnostics">
      <p className="pi-small pi-muted">Technical research record for your review. None of this appears in the client summary, email or PDF.</p>
      <table className="pi-table" style={{ marginBottom: 12 }}>
        <thead><tr><th>Module</th><th>Status</th><th>Started</th><th>Finished</th><th>Note</th></tr></thead>
        <tbody>
          {d.modules.map((m) => (
            <tr key={m.key}>
              <td data-label="Module">{m.key}</td>
              <td data-label="Status">{m.status}</td>
              <td data-label="Started">{fmt(m.startedAt)}</td>
              <td data-label="Finished">{fmt(m.finishedAt)}</td>
              <td data-label="Note">{m.error || (m.allottedSearches ? `${m.allottedSearches} searches allotted` : "-")}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {d.unavailableSources.length > 0 && (
        <>
          <div className="pi-label">Sources that couldn&apos;t be accessed ({d.unavailableSources.length})</div>
          <ul className="pi-small" style={{ paddingLeft: 18 }}>
            {d.unavailableSources.map((u, i) => <li key={i}><strong>{u.source}</strong> ({u.module}): {u.reason}{u.url ? <> - <a href={u.url} target="_blank" rel="noopener noreferrer">portal</a></> : null}</li>)}
          </ul>
        </>
      )}
      {d.notFound.length > 0 && (
        <>
          <div className="pi-label">Searched without a matching record ({d.notFound.length})</div>
          <ul className="pi-small" style={{ paddingLeft: 18 }}>
            {d.notFound.map((n, i) => <li key={i}><strong>{n.topic}</strong> ({n.module}): {n.searched}</li>)}
          </ul>
        </>
      )}
      {d.gapFindings.length > 0 && (
        <>
          <div className="pi-label">Findings recorded as research gaps ({d.gapFindings.length})</div>
          <ul className="pi-small" style={{ paddingLeft: 18 }}>{d.gapFindings.map((g) => <li key={g.id}>{g.title} ({g.status})</li>)}</ul>
        </>
      )}
      {d.reclassified.length > 0 && (
        <>
          <div className="pi-label">Status adjusted for display ({d.reclassified.length})</div>
          <ul className="pi-small" style={{ paddingLeft: 18 }}>{d.reclassified.map((g) => <li key={g.id}>{g.title}: {g.from} &rarr; {g.to} (third-party sources only)</li>)}</ul>
        </>
      )}
      {d.incompleteModules.length > 0 && (
        <p className="pi-small"><strong>Not completed:</strong> {d.incompleteModules.map((m) => `${m.title} (${m.reason})`).join("; ")}</p>
      )}
      {d.validationIssues.length > 0 && <p className="pi-small"><strong>Writer checks:</strong> {d.validationIssues.join("; ")}</p>}
      <p className="pi-small pi-muted">{d.sourceCount} sources · {d.findingCount} findings · research {fmt(d.researchStartedAt)} &ndash; {fmt(d.researchCompletedAt)}</p>
      {d.cost && (
        <p className="pi-small"><strong>Cost (admin):</strong> ${Number(d.cost.spentUsd || 0).toFixed(2)} of ${Number(d.cost.capUsd || 0).toFixed(2)} cap{d.models ? ` · models: ${Object.entries(d.models).map(([k, v]) => `${k} ${v}`).join(", ")}` : ""}</p>
      )}
    </Disclosure>
  );
}

export default function PiFullReport({ p, report, onEdit }) {
  const [editingKey, setEditingKey] = useState(null);
  const articleRef = useRef(null);
  const fr = p.fullReport;
  const ddEdits = report.agentEdits?.dueDiligence || {};

  const scrollTo = (id) => {
    const el = articleRef.current?.querySelector(`#${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!fr.sections.length) return <p className="pi-empty">The written report will appear here once research finishes.</p>;

  return (
    <div className="pi-reader">
      <nav className="pi-toc" aria-label="Report contents">
        {fr.sections.map((s) => <button type="button" key={s.key} onClick={() => scrollTo(`pi-sec-${s.key}`)}>{s.title}</button>)}
        {fr.sourceIndex.length > 0 && <button type="button" onClick={() => scrollTo("pi-sources")}>Sources</button>}
      </nav>

      <article className="pi-article" ref={articleRef}>
        {report.inputSnapshot?.agentPropertyNotes && (
          <div className="pi-note" style={{ marginBottom: 20 }}>
            <Icon name="user" />
            <div><strong>Your original notes:</strong> &ldquo;{report.inputSnapshot.agentPropertyNotes}&rdquo;</div>
          </div>
        )}

        {fr.sections.map((s) => {
          const topics = s.evidenceTopicIds.map((id) => Object.values(p.topics).find((t) => t.id === id)).filter(Boolean);
          const evidence = topics.flatMap((t) => t.findings);
          return (
            <section key={s.key} id={`pi-sec-${s.key}`} style={{ scrollMarginTop: 8 }}>
              <h3>
                <span style={{ flex: 1 }}>{s.title}</span>
                {s.edited && <span className="pi-badge neutral">Edited</span>}
                {onEdit && editingKey !== s.key && (
                  <button type="button" className="pi-link pi-small" onClick={() => setEditingKey(s.key)}><Icon name="pencil" /> Edit</button>
                )}
              </h3>
              {editingKey === s.key ? (
                <SectionEditor section={s} onEdit={onEdit} onDone={() => setEditingKey(null)} />
              ) : (
                s.paragraphs.map((para, i) => (
                  <p key={i}>
                    {para.text}
                    {para.refs.slice(0, 3).map((n) => (
                      <button type="button" key={n} className="pi-ref" onClick={() => scrollTo(`pi-src-${n}`)} aria-label={`Source ${n}`}>[{n}]</button>
                    ))}
                    {para.refs.length > 3 && <span className="pi-ref" title={`Also sources ${para.refs.slice(3).join(", ")}`}>+{para.refs.length - 3}</span>}
                  </p>
                ))
              )}

              {s.key === "agentRequested" && fr.concerns.length > 0 && (
                <table className="pi-table" style={{ margin: "6px 0 14px" }}>
                  <thead><tr><th>Your concern</th><th>What we found</th><th>Evidence</th><th>Follow-up</th></tr></thead>
                  <tbody>
                    {fr.concerns.map((c) => (
                      <tr key={c.concernId}>
                        <td data-label="Your concern" style={{ fontStyle: "italic" }}>&ldquo;{c.originalConcern}&rdquo;</td>
                        <td data-label="What we found">{c.findings}<SourceLinks sources={c.sources} max={2} label={null} /></td>
                        <td data-label="Evidence"><StatusBadge display={c.display} /></td>
                        <td data-label="Follow-up">{c.followUp || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {s.key === "dueDiligence" && p.allNextSteps.length > 0 && (
                <table className="pi-table" style={{ margin: "6px 0 14px" }}>
                  <thead><tr><th>Priority</th><th>Action</th><th>Why</th><th>Status</th></tr></thead>
                  <tbody>
                    {p.allNextSteps.map((d) => (
                      <tr key={`${d.index}-${d.action}`}>
                        <td data-label="Priority"><span className={`pi-badge ${d.priority === "high" ? "warning" : d.priority === "medium" ? "caution" : "neutral"}`}>{d.priority}</span></td>
                        <td data-label="Action"><strong>{d.action}</strong>{d.detail && <div className="pi-small pi-muted">{d.detail}</div>}</td>
                        <td data-label="Why">{d.reason || "-"}</td>
                        <td data-label="Status">
                          {d.index === null ? "-" : (
                            <Dropdown selection compact disabled={!onEdit} options={DD_OPTIONS} value={ddEdits[d.index]?.status || "open"} onChange={(e, { value }) => onEdit({ type: "dueDiligence", index: d.index, status: value })} />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {s.key === "dueDiligence" && fr.keyQuestions.length > 0 && (
                <>
                  <div className="pi-label" style={{ margin: "10px 0 4px" }}>Questions to ask</div>
                  <ul style={{ paddingLeft: 18, margin: "0 0 14px" }}>{fr.keyQuestions.map((q, i) => <li key={i} style={{ marginBottom: 4 }}>{q}</li>)}</ul>
                </>
              )}
              <EvidenceDisclosure findings={evidence} onEdit={onEdit} />
            </section>
          );
        })}

        <CommentaryEditor report={report} onEdit={onEdit} />

        {fr.sourceIndex.length > 0 && (
          <section id="pi-sources" style={{ marginTop: 28 }}>
            <h3>Sources</h3>
            <ol className="pi-small" style={{ paddingLeft: 22, margin: 0 }}>
              {fr.sourceIndex.map((s) => (
                <li key={s.id} id={`pi-src-${s.n}`} style={{ marginBottom: 4 }}>
                  {s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer">{s.label}</a> : s.label}
                  <span className="pi-muted"> · {s.tierLabel}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        <p className="pi-small pi-muted" style={{ marginTop: 24 }}>
          This report summarizes public records and other sources available when research was completed. It is not a home inspection, survey, appraisal, title review or legal opinion.
        </p>
        <Diagnostics d={p.diagnostics} />
      </article>
    </div>
  );
}
